import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Отправляем httpOnly cookie с каждым запросом axios
axios.defaults.withCredentials = true;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(null);
    localStorage.removeItem('has_session');
  }, []);

  // Восстанавливаем сессию из httpOnly cookie при запуске приложения
  useEffect(() => {
    // Очистка устаревших токенов из localStorage (миграция на httpOnly cookie)
    localStorage.removeItem('auth_token');
    localStorage.removeItem('current_user');
    localStorage.removeItem('user');

    const checkUser = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data.user);
        localStorage.setItem('has_session', '1');
      } catch (error) {
        setUser(null);
        localStorage.removeItem('has_session');
      }
      setLoading(false);
    };

    checkUser();
  }, []);

  const login = async (role, serviceType = null, accessToken = null, userData = null) => {
    try {
      let user;

      // If user data provided (from IIN login), use it directly - cookie is already set by server
      if (userData) {
        user = userData;
      } else {
        // Otherwise use demo login - server sets httpOnly cookie
        const loginData = { role };
        if (serviceType) {
          loginData.service_type = serviceType;
        }

        const response = await axios.post(`${API}/auth/demo-login`, loginData);
        user = response.data.user;
      }

      setUser(user);
      localStorage.setItem('has_session', '1');

      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Network error'
      };
    }
  };

  const isResident = useCallback(() => user?.role === 'resident', [user]);
  const isService = useCallback(() => user?.role === 'service', [user]);
  const isAdmin = useCallback(() => user?.role === 'admin', [user]);

  const hasRole = useCallback((roles) => {
    if (!user) return false;
    return Array.isArray(roles) ? roles.includes(user.role) : user.role === roles;
  }, [user]);

  const value = {
    user,
    loading,
    login,
    logout,
    isResident,
    isService,
    isAdmin,
    hasRole,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
